<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetContractCompanyData',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    contract: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
    noLegend: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);
  const b_1040167445267876_dataSource = computed(() => ({
    name: 'suggest_company',
    parameters: {
      role: 'own',
    },
  }));

  const $data = computed(() => ({
    ux: ux.value,
    b_1040167445267876_dataSource: b_1040167445267876_dataSource.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'ux' },
      { name: 'b_1040167445267876_dataSource', nodeId: '1040167445267876' },
    ],
    loaders: {},
    ux: {},
    dataRefs: { ux, b_1040167445267876_dataSource },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );
  watch(
    () => props.noLegend,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('noLegend');
    }
  );

  const act_1040167445267876_setData_gcicmtaks_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'contract.company',
      value: $event.data.response.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1040167445267876_request_5d71933t5_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'read_company',
      parameters: {
        id: $event.data.value.id,
      },
      headers: {
        accept: 'application/vnd.itp.contractparty+json',
      },
    };
  };

  const act_7315092382398562_reloadComponentData_9hmxe2i1n =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_j0vilz8gd =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_9hmxe2i1n,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_j0vilz8gd],
    event: 'reload',
  });
  const act_1040167445267876_setData_gcicmtaks = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_gcicmtaks_getActionArgs,
  });
  const inst_evh_1040167445267876_success_n37r4ztk3 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_gcicmtaks,
      event: 'success',
      displayName: 'setData',
    });
  const act_1040167445267876_request_5d71933t5 = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_5d71933t5_getActionArgs,
    events: [inst_evh_1040167445267876_success_n37r4ztk3],
  });
  const inst_evh_1040167445267876_optionSelected_4odyy4pco =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_5d71933t5,
      event: 'optionSelected',
      displayName: 'request',
    });
  const inst_evh_1040167445267876_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1040167445267876_optionSelected_4odyy4pco],
      event: 'optionSelected',
    });

  const actions = {
    inst_evh_7315092382398562_reload_j0vilz8gd,
    inst_evh_7315092382398562_reload,
    inst_evh_1040167445267876_success_n37r4ztk3,
    inst_evh_1040167445267876_optionSelected_4odyy4pco,
    inst_evh_1040167445267876_optionSelected,
  };

  const evh_7315092382398562_reload_j0vilz8gd = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_j0vilz8gd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_n37r4ztk3 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_n37r4ztk3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_optionSelected_4odyy4pco = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1040167445267876_optionSelected_4odyy4pco.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_optionSelected = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <legend v-if="!!noLegend">
      {{ pgettext('Contract', 'Lessor') }}
    </legend>
    <ItpFormGroup
      name="company"
      :label="pgettext('Contract', 'Company')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      required
    >
      <ItpSelect2
        name="company"
        v-model="contract.company"
        :value="contract.company"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.company =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        bind-object
        :dataSource="b_1040167445267876_dataSource"
        textField="name"
        prefetch
        required
        optionTemplate="<UcSuggestCompanyOption :option=option></UcSuggestCompanyOption>"
        use-option-template-for-selected-option
        @optionSelected="evh_1040167445267876_optionSelected($event, {})"
      >
      </ItpSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      name="head_office"
      :label="pgettext('Company', 'Head office')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      v-if="contract.company"
    >
      <ItpFormInput
        name="head_office"
        v-model="contract.company.head_office"
        :value="contract.company.head_office"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.company.head_office =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="branch_office"
      :label="pgettext('Company', 'Branch office')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      v-if="contract.company"
    >
      <ItpFormInput
        name="branch_office"
        v-model="contract.company.branch_office"
        :value="contract.company.branch_office"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.company.branch_office =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="statistical_code"
      :label="pgettext('Company', 'Statistical code')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
      v-if="contract.company"
    >
      <ItpFormInput
        name="statistical_code"
        v-model="contract.company.statistical_code"
        :value="contract.company.statistical_code"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.company.statistical_code =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="registration_number"
      :label="pgettext('Company', 'Registration number')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
      v-if="contract.company"
    >
      <ItpFormInput
        name="registration_number"
        v-model="contract.company.registration_number"
        :value="contract.company.registration_number"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.company.registration_number =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="account_number"
      :label="pgettext('Company', 'Account number')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      v-if="contract.company"
    >
      <ItpFormInput
        name="account_number"
        v-model="contract.company.account_number"
        :value="contract.company.account_number"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.company.account_number =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="representative"
      :label="pgettext('Company', 'Representative')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      v-if="contract.company"
    >
      <ItpFormTextarea
        name="representative"
        v-model="contract.company.representative"
        :value="contract.company.representative"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.company.representative =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormTextarea>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
